import { useAuth } from "@/hooks/use-auth-simple";
import { safeParseJSON } from "@/lib/safeJson";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Conversation, Message } from "@shared/schema";
import {
  Search, MessageSquare, ChevronRight, ChevronDown,
  Copy, Loader2, Trash2, User as UserIcon,
  LogOut, Shield, Send, ArrowUp, FileText,
  ThumbsUp, ThumbsDown,
  ExternalLink, Paperclip, Check, Share2, Plus,
  Mic, Lightbulb, Clock, Settings, ClipboardList, HelpCircle, ChevronsLeft, Pencil, Sun, Moon, Trophy, Mail, X
} from "lucide-react";
import { format, isToday, isYesterday, startOfWeek, isWithinInterval, parseISO } from "date-fns";
import { Link } from "wouter";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { GrokLogo } from "@/components/grok-logo";
import { useTheme } from "next-themes";

// Speech Recognition Types
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionErrorEvent) => void;
  onend: () => void;
  onstart: () => void;
}

interface SpeechRecognitionEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message: string;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

declare global {
  interface Window {
    SpeechRecognition: {
      new(): SpeechRecognition;
    };
    webkitSpeechRecognition: {
      new(): SpeechRecognition;
    };
  }
  // Add Salesforce Embedded Service types
  interface Window {
    embeddedservice_bootstrap?: {
      settings: {
        language: string;
        hideChatButtonOnLoad: boolean;
      };
      init: (
        orgId: string,
        serviceName: string,
        baseUrl: string,
        settings: {
          scrt2URL: string;
        }
      ) => void;
      utilAPI: {
        launchChat: () => void;
        showChatButton: () => void;
      };
    };
  }
}

// Types for SSE data
type SSETokenData = { type: 'token'; content: string };
type SSEDoneData = {
  type: 'done';
  suggestedQuestions?: string[];
  assistantMessage?: { id: string };
};
type SSEErrorData = { type: 'error'; message: string };
type SSEData = SSETokenData | SSEDoneData | SSEErrorData | Record<string, unknown>;

type SSEStatusData = { message: string };
type SSEStepData = { step: string; status: 'active' | 'pending' | 'complete'; message?: string };
type SSEChunkData = { content: string };
type SSEContentData = { content: string };
type SSEDoneStreamData = {
  suggestedQuestions?: string[];
  assistantMessage?: { id: string };
};
type SSEErrorStreamData = { message: string };
type SSEStreamData =
  | SSEStatusData
  | SSEStepData
  | SSEChunkData
  | SSEContentData
  | SSEDoneStreamData
  | SSEErrorStreamData
  | Record<string, unknown>;

type GroupedConversations = {
  today: Conversation[];
  yesterday: Conversation[];
  thisWeek: Conversation[];
  thisMonth: Conversation[];
  older: Conversation[];
};

// Helper function to group conversations by date
function groupConversationsByDate(conversations: Conversation[]): GroupedConversations {
  const now = new Date();
  const startOfThisWeek = startOfWeek(now, { weekStartsOn: 0 });
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const grouped: GroupedConversations = {
    today: [],
    yesterday: [],
    thisWeek: [],
    thisMonth: [],
    older: [],
  };

  conversations.forEach(conv => {
    if (!conv.updatedAt) {
      grouped.older.push(conv);
      return;
    }

    const dateStr = typeof conv.updatedAt === 'string' ? conv.updatedAt : conv.updatedAt.toISOString();
    const date = parseISO(dateStr);

    if (isToday(date)) {
      grouped.today.push(conv);
    } else if (isYesterday(date)) {
      grouped.yesterday.push(conv);
    } else if (isWithinInterval(date, { start: startOfThisWeek, end: now })) {
      grouped.thisWeek.push(conv);
    } else if (date >= startOfThisMonth) {
      grouped.thisMonth.push(conv);
    } else {
      grouped.older.push(conv);
    }
  });

  return grouped;
}

export default function ChatPage() {
  const { user, logoutMutation } = useAuth();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [themeMounted, setThemeMounted] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({
    today: true,
    yesterday: true,
    thisWeek: false,
    october: false,
    june: false,
  });
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  useEffect(() => {
    setMounted(true);
    setThemeMounted(true);
  }, []);

  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const [productFilter, setProductFilter] = useState<string>("all");
  const [suggestedQuestions, setSuggestedQuestions] = useState<{ messageId: string; questions: string[] } | null>(null);
  const [messageRatings, setMessageRatings] = useState<Record<string, 'up' | 'down'>>({});
  const [showCommentInput, setShowCommentInput] = useState<Record<string, boolean>>({});
  const [ratingComments, setRatingComments] = useState<Record<string, string>>({});
  const [streamingMessage, setStreamingMessage] = useState<string>("");
  const [streamingStatus, setStreamingStatus] = useState<string>("Typing...");
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentSubHeading, setCurrentSubHeading] = useState<string>("");
  const [subHeadingPool, setSubHeadingPool] = useState<string[]>([]);
  const [processingSteps, setProcessingSteps] = useState<Array<{ step: string; status: 'pending' | 'active' | 'complete'; message?: string }>>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [productFilterOpen, setProductFilterOpen] = useState(false);
  const [composingResponseFor, setComposingResponseFor] = useState<string | null>(null);
  const [composedResponse, setComposedResponse] = useState<string | null>(null);
  const [showComposeDialog, setShowComposeDialog] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [isRewriting, setIsRewriting] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [formattingMessageId, setFormattingMessageId] = useState<string | null>(null);
  const [formattingType, setFormattingType] = useState<'email' | 'text' | null>(null);
  const [formattedMessage, setFormattedMessage] = useState<string | null>(null);
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isListening, setIsListening] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isListeningRef = useRef(false);

  // TLS Guidelines analyzing sub-headings (rotation pool)
  const TLS_SUB_HEADINGS = [
    "Reviewing loan requirements",
    "Scanning borrower criteria",
    "Checking documentation standards",
    "Evaluating credit considerations",
    "Confirming income guidelines",
    "Reviewing asset and reserve notes",
    "Verifying property guidance",
    "Reviewing appraisal references",
    "Checking disclosure expectations",
    "Reviewing underwriting guidance",
    "Reviewing AUS guidance",
    "Checking closing requirements",
    "Reviewing post-closing notes",
    "Confirming overall guideline fit",
    "Reviewing rate and pricing notes",
  ];

  // Shuffle array helper
  const shuffleArray = (array: string[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Initialize sub-heading pool on mount
  useEffect(() => {
    if (subHeadingPool.length === 0) {
      const shuffled = shuffleArray(TLS_SUB_HEADINGS);
      setSubHeadingPool(shuffled);
      setCurrentSubHeading(shuffled[0]);
    }
  }, []);

  // Rotate sub-headings every 4 seconds when streaming
  useEffect(() => {
    if (!isStreaming) return;

    const interval = setInterval(() => {
      setSubHeadingPool(prevPool => {
        const remainingPool = prevPool.slice(1);

        if (remainingPool.length === 0) {
          const reshuffled = shuffleArray(TLS_SUB_HEADINGS);
          setCurrentSubHeading(reshuffled[0]);
          return reshuffled;
        }

        setCurrentSubHeading(remainingPool[0]);
        return remainingPool;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [isStreaming]);

  const { data: conversations = [], isLoading: conversationsLoading } = useQuery<Conversation[]>({
    queryKey: ["/api/conversations"],
  });

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const query = searchQuery.toLowerCase();
    return conversations.filter(conv =>
      conv.title.toLowerCase().includes(query)
    );
  }, [conversations, searchQuery]);

  const groupedConversations = useMemo(() => {
    return groupConversationsByDate(filteredConversations);
  }, [filteredConversations]);

  const { data: messages = [], isLoading: messagesLoading } = useQuery<Message[]>({
    queryKey: ["/api/conversations", currentConversationId, "messages"],
    enabled: !!currentConversationId,
  });

  // Leaderboard query
  const { data: leaderboardData } = useQuery<{ period: string; users: any[] }>({
    queryKey: ['/api/leaderboard'],
    enabled: showLeaderboard,
  });

  const createConversationMutation = useMutation({
    mutationFn: async (title: string) => {
      const res = await apiRequest("POST", "/api/conversations", { title });
      return await res.json();
    },
    onSuccess: (conversation: Conversation) => {
      queryClient.invalidateQueries({ queryKey: ["/api/conversations"] });
      setCurrentConversationId(conversation.id);
    },
  });

  const streamMessage = async (conversationId: string, content: string, checkExceptions = false) => {
    if (!user) {
      toast({
        title: "Authentication Error",
        description: "Please log in to send messages",
        variant: "destructive",
      });
      return;
    }

    if (!conversationId || !content?.trim()) {
      toast({
        title: "Invalid Input",
        description: "Please provide a valid message",
        variant: "destructive",
      });
      return;
    }

    setIsStreaming(true);
    setStreamingMessage("");
    setStreamingStatus("Searching...");
    setMessageInput("");
    // Initialize 4 processing steps - 3 search agents + answer generation
    setProcessingSteps([
      { step: 'Searching guidelines', status: 'pending', message: 'Waiting...' },
      { step: 'Searching exceptions', status: 'pending', message: 'Waiting...' },
      { step: 'Searching web information', status: 'pending', message: 'Waiting...' },
      { step: 'Generating answer', status: 'pending', message: 'Waiting for searches...' }
    ]);

    try {
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/conversations/${conversationId}/messages/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content,
          checkExceptions,
          productFilter: productFilter === "all" ? undefined : productFilter
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to stream message');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          const lines = eventBlock.split('\n');
          let eventType = '';
          let eventData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6).trim();
            }
          }

          if (eventType && eventData) {
            try {
              const data = safeParseJSON<SSEStreamData>(eventData, {});

              if (eventType === 'step') {
                // Update processing step status and message
                const stepData = data as SSEStepData;
                setProcessingSteps(prev => {
                  return prev.map(s => 
                    s.step === stepData.step 
                      ? { ...s, status: stepData.status, message: stepData.message || s.message } 
                      : s
                  );
                });
              } else if (eventType === 'chunk') {
                // Continuously append chunks to streaming message
                setStreamingMessage(prev => prev + (data as SSEChunkData).content);
              } else if (eventType === 'done') {
                queryClient.invalidateQueries({ queryKey: ["/api/conversations", conversationId, "messages"] });
                queryClient.invalidateQueries({ queryKey: ["/api/conversations"] });

                const doneData = data as SSEDoneStreamData;
                if (doneData.suggestedQuestions && Array.isArray(doneData.suggestedQuestions) && doneData.suggestedQuestions.length > 0 && doneData.assistantMessage) {
                  setSuggestedQuestions({
                    messageId: doneData.assistantMessage.id,
                    questions: doneData.suggestedQuestions
                  });
                } else {
                  setSuggestedQuestions(null);
                }

                setStreamingMessage("");
                setIsStreaming(false);
                setProcessingSteps([]);
              } else if (eventType === 'error') {
                toast({
                  title: "Error",
                  description: (data as SSEErrorStreamData).message,
                  variant: "destructive",
                });
                setStreamingMessage("");
                setIsStreaming(false);
                setProcessingSteps([]);
              }
            } catch (parseError) {
              // Silently handle parse errors for malformed SSE data
              // This prevents UI disruption from backend formatting issues
              if (process.env.NODE_ENV === 'development') {
                console.error('Error parsing SSE data:', parseError, eventData);
              }
            }
          }
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setStreamingMessage("");
      setIsStreaming(false);
      setProcessingSteps([]);
    }
  };

  useEffect(() => {
    // Improved scroll behavior for mobile with proper timing
    const scrollToBottom = () => {
      if (scrollRef.current) {
        try {
          const scrollContainer = scrollRef.current;
          const scrollHeight = scrollContainer.scrollHeight;
          const clientHeight = scrollContainer.clientHeight;

          // Use smooth scroll on mobile for better UX
          scrollContainer.scrollTo({
            top: scrollHeight - clientHeight,
            behavior: 'smooth'
          });
        } catch (error) {
          // Fallback for browsers that don't support scrollTo
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        }
      }
    };

    // Use setTimeout to ensure DOM has updated, especially important on mobile
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [messages, streamingMessage]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + "px";
    }
  }, [messageInput]);

  const handleSendMessage = (checkExceptions = false) => {
    if (!messageInput.trim() || isStreaming) return;

    const content = messageInput;

    if (!currentConversationId) {
      createConversationMutation.mutate("New Conversation", {
        onSuccess: (conversation: Conversation) => {
          streamMessage(conversation.id, content, checkExceptions);
        },
      });
    } else {
      streamMessage(currentConversationId, content, checkExceptions);
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    if (isStreaming) return;

    setSuggestedQuestions(null);

    const checkExceptions = question === "Check Exceptions";
    const content = checkExceptions ? "Please check for exceptions to the previous guidelines." : question;

    if (!currentConversationId) {
      createConversationMutation.mutate("New Conversation", {
        onSuccess: (conversation: Conversation) => {
          streamMessage(conversation.id, content, checkExceptions);
        },
      });
    } else {
      streamMessage(currentConversationId, content, checkExceptions);
    }

    setMessageInput("");
  };

  const rateMessageMutation = useMutation({
    mutationFn: async ({ messageId, vote, comment }: { messageId: string; vote: 'up' | 'down'; comment?: string }) => {
      const res = await apiRequest("POST", `/api/messages/${messageId}/rate`, { vote, comment });
      return await res.json();
    },
    onSuccess: (_, variables) => {
      setMessageRatings(prev => ({ ...prev, [variables.messageId]: variables.vote }));
      setShowCommentInput(prev => ({ ...prev, [variables.messageId]: false }));
      setRatingComments(prev => ({ ...prev, [variables.messageId]: '' }));
      queryClient.invalidateQueries({ queryKey: ['/api/admin/feedback'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/feedback/stats'] });
      toast({
        title: "Thank you for your feedback!",
        description: "Your rating helps improve our responses.",
      });
    },
  });

  const composeResponseMutation = useMutation({
    mutationFn: async (messageId: string) => {
      const res = await apiRequest("POST", `/api/messages/${messageId}/compose-response`, {});
      return await res.json();
    },
    onSuccess: (data) => {
      setComposedResponse(data.composedResponse);
      setShowComposeDialog(true);
      setIsComposing(false);
      setComposingResponseFor(null);
    },
    onError: (error: any) => {
      setIsComposing(false);
      setComposingResponseFor(null);
      toast({
        title: "Error",
        description: error.message || "Failed to compose response. Please try again.",
        variant: "destructive",
      });
    },
  });

  const formatMessageMutation = useMutation({
    mutationFn: async ({ messageId, format }: { messageId: string; format: 'email' | 'text' }) => {
      const res = await apiRequest("POST", `/api/messages/${messageId}/format`, { format });
      return await res.json();
    },
    onSuccess: (data) => {
      setFormattedMessage(data.formattedMessage);
      setFormattingType(data.format);
      setShowFormatDialog(true);
      setFormattingMessageId(null);
    },
    onError: (error: any) => {
      setFormattingMessageId(null);
      setFormattingType(null);
      toast({
        title: "Error",
        description: error.message || "Failed to format message. Please try again.",
        variant: "destructive",
      });
    },
  });

  const rewriteResponseMutation = useMutation({
    mutationFn: async ({ messageId, currentResponse, modification }: {
      messageId: string;
      currentResponse: string;
      modification: string
    }) => {
      const res = await apiRequest("POST", `/api/messages/${messageId}/rewrite-response`, {
        currentResponse,
        modification
      });
      const data = await res.json();
      return data.rewrittenResponse;
    },
    onSuccess: (rewrittenText: string) => {
      setComposedResponse(rewrittenText);
      setIsRewriting(false);
      toast({
        title: "Response updated",
        description: "The response has been modified.",
      });
    },
    onError: (error: Error) => {
      setIsRewriting(false);
      toast({
        title: "Error",
        description: error.message || "Failed to rewrite response. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleComposeResponse = (messageId: string) => {
    setSelectedMessageId(messageId);
    setComposingResponseFor(messageId);
    setIsComposing(true);
    composeResponseMutation.mutate(messageId);
  };

  const handleFormatMessage = (messageId: string, format: 'email' | 'text') => {
    setFormattingMessageId(messageId);
    setFormattingType(format);
    formatMessageMutation.mutate({ messageId, format });
  };

  const deleteConversationMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      await apiRequest("DELETE", `/api/conversations/${conversationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/conversations"] });
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
      if (conversationToDelete === currentConversationId) {
        setCurrentConversationId(null);
      }
    },
  });

  const handleDeleteConversation = (conversationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setConversationToDelete(conversationId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (conversationToDelete) {
      deleteConversationMutation.mutate(conversationToDelete);
    }
  };

  const handleNewConversation = () => {
    setCurrentConversationId(null);
    setMessageInput("");
    setSelectedFile(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      toast({
        title: "File Selected",
        description: `${file.name} (${(file.size / 1024).toFixed(2)} KB)`,
      });
    }
  };


  // Initialize Speech Recognition
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check if Speech Recognition is available
    const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn('Speech Recognition API not available in this browser');
      return;
    }

    try {
      const recognition = new SpeechRecognition();

      // Use non-continuous mode for better reliability
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          const transcript = result[0].transcript;

          if (result.isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        // Update the input field with the transcript
        const transcript = finalTranscript || interimTranscript;
        if (transcript.trim()) {
          setMessageInput(prev => {
            // If we have final results, replace; otherwise append interim
            return finalTranscript ? finalTranscript.trim() : (prev + ' ' + interimTranscript).trim();
          });
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error, event.message);

        // Reset listening state
        setIsListening(false);
        isListeningRef.current = false;

        // Handle specific error types
        switch (event.error) {
          case 'no-speech':
            // No speech detected - this is normal, don't show error
            return;

          case 'audio-capture':
            toast({
              title: "Microphone not found",
              description: "No microphone detected. Please connect a microphone and try again.",
              variant: "destructive",
            });
            break;

          case 'not-allowed':
            toast({
              title: "Microphone permission denied",
              description: "Please allow microphone access in your browser settings to use voice input.",
              variant: "destructive",
            });
            break;

          case 'network':
            toast({
              title: "Connection error",
              description: "Could not connect to speech recognition service. Please check your internet connection and try again.",
              variant: "destructive",
            });
            break;

          case 'aborted':
            // User or system aborted - this is fine, don't show error
            break;

          case 'service-not-allowed':
            toast({
              title: "Service not allowed",
              description: "Speech recognition service is not allowed. Please check your browser settings.",
              variant: "destructive",
            });
            break;

          default:
            toast({
              title: "Speech recognition error",
              description: `Error: ${event.error}. ${event.message || 'Please try again.'}`,
              variant: "destructive",
            });
        }
      };

      recognition.onend = () => {
        // Stop listening when recognition ends naturally
        console.log('Speech recognition ended');
        setIsListening(false);
        isListeningRef.current = false;
      };

      recognition.onstart = () => {
        console.log('Speech recognition started successfully');
        setIsListening(true);
        isListeningRef.current = true;
      };

      recognitionRef.current = recognition;
    } catch (error: any) {
      console.error('Failed to initialize Speech Recognition:', error);
      toast({
        title: "Voice input unavailable",
        description: "Failed to initialize speech recognition. Please refresh the page and try again.",
        variant: "destructive",
      });
    }

    return () => {
      if (recognitionRef.current) {
        try {
          isListeningRef.current = false;
          recognitionRef.current.stop();
          recognitionRef.current.abort();
        } catch (e) {
          // Ignore errors on cleanup
        }
        recognitionRef.current = null;
      }
    };
  }, [toast]);

  const handleVoiceClick = async () => {
    // Check if we're in a secure context (required for microphone access)
    if (!window.isSecureContext && window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      toast({
        title: "Secure connection required",
        description: "Voice input requires a secure connection (HTTPS). Please use HTTPS or localhost.",
        variant: "destructive",
      });
      return;
    }

    // Check if Speech Recognition is available
    if (!recognitionRef.current) {
      const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;

      if (!SpeechRecognition) {
        toast({
          title: "Voice input not supported",
          description: "Your browser does not support speech recognition. Please use Chrome, Edge, or Safari.",
          variant: "destructive",
        });
        return;
      }

      // Try to initialize if not already done
      try {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.maxAlternatives = 1;

        recognition.onresult = (event: SpeechRecognitionEvent) => {
          let finalTranscript = '';
          let interimTranscript = '';

          for (let i = 0; i < event.results.length; i++) {
            const result = event.results[i];
            const transcript = result[0].transcript;

            if (result.isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }

          // Only update input on final transcript to avoid duplicates
          if (finalTranscript.trim()) {
            setMessageInput(prev => {
              // Append to existing messageinput if there's already text
              const newText = prev ? prev + ' ' + finalTranscript.trim() : finalTranscript.trim();
              return newText;
            });
          }
        };

        recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
          isListeningRef.current = false;

          if (event.error === 'not-allowed') {
            toast({
              title: "Microphone permission denied",
              description: "Please allow microphone access in your browser settings.",
              variant: "destructive",
            });
          } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
            toast({
              title: "Voice input error",
              description: `Error: ${event.error}. Please try again.`,
              variant: "destructive",
            });
          }
        };

        recognition.onend = () => {
          console.log('Speech recognition ended');
          setIsListening(false);
          isListeningRef.current = false;
        };

        recognition.onstart = () => {
          setIsListening(true);
          isListeningRef.current = true;
        };

        recognitionRef.current = recognition;
      } catch (error: any) {
        console.error('Failed to initialize recognition:', error);
        toast({
          title: "Voice input unavailable",
          description: "Failed to initialize speech recognition. Please refresh the page.",
          variant: "destructive",
        });
        return;
      }
    }

    if (isListening) {
      // Stop listening
      try {
        isListeningRef.current = false;
        if (recognitionRef.current) {
          recognitionRef.current.stop();
          recognitionRef.current.abort();
        }
        setIsListening(false);
        toast({
          title: "Stopped listening",
          description: "Voice input stopped.",
          variant: "default",
        });
      } catch (error) {
        console.error('Error stopping recognition:', error);
        setIsListening(false);
        isListeningRef.current = false;
      }
    } else {
      // Request microphone permission first
      try {
        // Check if getUserMedia is available
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('getUserMedia is not available in this browser');
        }

        // Request microphone access to ensure permission is granted
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        // Stop the stream immediately - we just needed permission
        stream.getTracks().forEach(track => track.stop());

        // Small delay to ensure permission is registered
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error: any) {
        console.error('Microphone permission error:', error);
        setIsListening(false);
        isListeningRef.current = false;

        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          toast({
            title: "Microphone permission denied",
            description: "Please allow microphone access in your browser settings to use voice input.",
            variant: "destructive",
          });
        } else if (error.name === 'NotFoundError') {
          toast({
            title: "No microphone found",
            description: "Please connect a microphone and try again.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Microphone access error",
            description: error.message || "Could not access microphone. Please check your settings.",
            variant: "destructive",
          });
        }
        return;
      }

      // Start listening
      try {
        if (!recognitionRef.current) {
          throw new Error('Speech recognition not initialized');
        }

        isListeningRef.current = true;
        recognitionRef.current.start();
        console.log('Speech recognition started');
      } catch (error: any) {
        console.error('Error starting speech recognition:', error);
        setIsListening(false);
        isListeningRef.current = false;

        if (error?.message?.includes('already started') || error?.message?.includes('started')) {
          // Recognition was already running
          setIsListening(true);
          isListeningRef.current = true;
        } else {
          toast({
            title: "Error starting voice input",
            description: error?.message || "Could not start voice input. Please try again.",
            variant: "destructive",
          });
        }
      }
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleHistory = () => setHistoryExpanded(!historyExpanded);
  const toggleGroup = (group: 'today' | 'yesterday' | 'thisWeek' | 'october' | 'june') => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const copyToClipboard = async (text: string, messageId: string) => {
    if (!text || !messageId) return;

    try {
      // Check if clipboard API is available
      if (!navigator.clipboard) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      } else {
        await navigator.clipboard.writeText(text);
      }

      setCopiedMessageId(messageId);
      toast({
        title: "Copied!",
        description: "Message copied to clipboard",
      });
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy message. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!mounted) {
    return null;
  }

  // Render history group
  const renderHistoryGroup = (title: string, conversations: Conversation[], groupKey: 'today' | 'yesterday' | 'thisWeek' | 'october' | 'june') => {
    if (conversations.length === 0) return null;

    const isExpanded = expandedGroups[groupKey];

    return (
      <div className="mb-2">
        <button
          onClick={() => toggleGroup(groupKey)}
          className="w-full flex items-center justify-between px-2 py-1.5 text-xs text-muted-foreground font-medium uppercase tracking-wide hover:text-foreground cursor-pointer transition-colors duration-150"
        >
          <span>{title}</span>
          <ChevronRight
            className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : 'rotate-0'}`}
          />
        </button>
        {isExpanded && (
          <div className="space-y-0.5 mt-1">
            {conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => setCurrentConversationId(conv.id)}
                className={`w-full text-left px-2 py-1.5 rounded-md text-sm overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer transition-all duration-150 ${currentConversationId === conv.id
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
              >
                <div className="truncate">{conv.title}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleSignOut = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        window.location.href = 'https://chat.tlstoday.com';
      }
    });
  };

  // Animation constants
  const springTransition = {
    type: "spring",
    stiffness: 300,
    damping: 30,
    mass: 1
  };

  const messageAnimation = {
    initial: { opacity: 0, y: 20, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } },
    transition: springTransition
  };

  return (
    <div className="h-screen flex bg-background text-foreground" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif' }}>
      {/* Mobile Header - Grok Style Dark Theme */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-card border-b border-border px-4 py-3 z-50 flex items-center justify-between" style={{ paddingTop: 'max(12px, calc(12px + env(safe-area-inset-top, 0px)))', paddingLeft: 'max(16px, calc(16px + env(safe-area-inset-left, 0px)))', paddingRight: 'max(16px, calc(16px + env(safe-area-inset-right, 0px)))' }}>
        <h1 className="text-lg font-semibold text-foreground">Ask</h1>
        <div className="relative">
          <button
            onClick={() => setShowAvatarMenu(!showAvatarMenu)}
            className="flex items-center justify-center px-2 py-1.5 rounded-md hover:bg-muted transition-colors cursor-pointer touch-manipulation"
            title="User Menu"
            style={{ minWidth: '44px', minHeight: '44px' }}
          >
            <div className="w-8 h-8 rounded-full bg-muted border border-border flex items-center justify-center">
              <UserIcon className="w-4 h-4 text-muted-foreground" />
            </div>
          </button>

          {showAvatarMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowAvatarMenu(false)}
              />
              <div className="absolute top-full right-0 mt-2 w-72 sm:w-64 bg-card border border-border rounded-lg shadow-xl py-2 z-20" style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', right: '0', left: 'auto' }}>
                <div className="px-4 py-3 border-b border-border">
                  <div className="text-sm font-medium text-foreground">{user?.username || 'User'}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{user?.isAdmin ? 'Admin' : 'User'}</div>
                </div>
                {/* Settings label removed – options below act as settings directly */}
                {themeMounted && (
                  <div className="px-4 py-2.5 flex items-center gap-3 min-h-[44px]">
                    <span className="text-sm text-muted-foreground flex-1 text-left">Dark Mode</span>
                    <Switch
                      checked={theme === 'dark'}
                      onCheckedChange={(checked) => {
                        setTheme(checked ? 'dark' : 'light');
                      }}
                      className="flex-shrink-0 data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted"
                    />
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowAvatarMenu(false);
                    toast({
                      title: "Coming Soon",
                      description: "Help will be available soon.",
                    });
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-2 transition-colors min-h-[44px]"
                >
                  <HelpCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Help</span>
                  <ChevronRight className="w-3 h-3 ml-auto" />
                </button>
                {user?.isAdmin && (
                  <Link href="/admin" onClick={() => setShowAvatarMenu(false)}>
                    <button className="w-full text-left px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-2 transition-colors min-h-[44px]">
                      <Shield className="w-4 h-4" />
                      Admin Dashboard
                    </button>
                  </Link>
                )}
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-2 transition-colors min-h-[44px]"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      {/* Sidebar - Theme Aware */}
      <aside className="hidden md:flex w-[240px] bg-card border-r border-border flex-col overflow-y-auto">
        {/* Search */}
        <div className="px-3 py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search ⌘K"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-background border border-border rounded-md pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring focus:outline-none w-full"
            />
          </div>
        </div>


        {/* Navigation Links - Grok Style */}
        <div className="px-2 mb-2">
          <button
            onClick={handleNewConversation}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors duration-150 cursor-pointer"
          >
            <Pencil className="w-4 h-4" strokeWidth={2} />
            <span>Chat</span>
          </button>
          <button
            onClick={() => setShowLeaderboard(!showLeaderboard)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150 cursor-pointer"
          >
            <Trophy className="w-4 h-4" />
            <span>Leaderboard</span>
          </button>
          <Link href="/feature-requests">
            <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150 cursor-pointer">
              <Lightbulb className="w-4 h-4" />
              <span>Submit an Idea</span>
            </button>
          </Link>
          <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150 cursor-pointer">
            <Clock className="w-4 h-4" />
            <span>History</span>
          </button>
        </div>

        {/* History Section - Grok Style */}
        <div className="flex-1 overflow-y-auto px-2">
          <div className="px-2 py-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">History</div>
            {renderHistoryGroup("Today", groupedConversations.today, 'today')}
            {renderHistoryGroup("Yesterday", groupedConversations.yesterday, 'yesterday')}
            {renderHistoryGroup("This Week", groupedConversations.thisWeek, 'thisWeek')}
            {renderHistoryGroup("August", groupedConversations.thisMonth, 'october')}
            {renderHistoryGroup("Older", groupedConversations.older, 'june')}
          </div>
        </div>

        {/* User Profile - Bottom Grok Style */}
        <div className="p-3 border-t border-border relative">
          <button
            onClick={() => setShowAvatarMenu(!showAvatarMenu)}
            className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted transition-colors cursor-pointer"
            title="User Menu"
          >
            <div className="w-8 h-8 rounded-full bg-muted border border-border flex items-center justify-center">
              <UserIcon className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-medium text-foreground">{user?.username || 'User'}</div>
              <div className="text-xs text-muted-foreground">{user?.isAdmin ? 'Admin' : 'User'}</div>
            </div>
            <ChevronsLeft className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${showAvatarMenu ? 'rotate-180' : ''}`} />
          </button>

          {showAvatarMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowAvatarMenu(false)}
              />
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-card border border-border rounded-lg shadow-xl py-2 z-20">
                <div className="px-4 py-2 border-b border-border">
                  <div className="text-sm font-medium text-foreground">{user?.username || 'User'}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{user?.isAdmin ? 'Admin' : 'User'}</div>
                </div>
                {themeMounted && (
                  <div className="px-4 py-2 flex items-center gap-3 min-h-[44px]">
                    <span className="text-sm text-muted-foreground flex-1 text-left">Dark Mode</span>
                    <Switch
                      checked={theme === 'dark'}
                      onCheckedChange={(checked) => {
                        setTheme(checked ? 'dark' : 'light');
                      }}
                      className="flex-shrink-0 data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted"
                    />
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowAvatarMenu(false);
                    toast({
                      title: "Coming Soon",
                      description: "Help will be available soon.",
                    });
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-2 transition-colors"
                >
                  <HelpCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Help</span>
                  <ChevronRight className="w-3 h-3 ml-auto" />
                </button>
                {user?.isAdmin && (
                  <Link href="/admin" onClick={() => setShowAvatarMenu(false)}>
                    <button className="w-full text-left px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-2 transition-colors">
                      <Shield className="w-4 h-4" />
                      Admin Dashboard
                    </button>
                  </Link>
                )}
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-2 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </aside>

      {/* Main Chat Area - Theme Aware */}
      <main className="flex-1 flex flex-col overflow-hidden bg-background md:pt-0" style={{ paddingTop: 'max(56px, calc(56px + env(safe-area-inset-top, 0px)))' }}>
        {/* Chat Content */}
        <ScrollArea className="flex-1 pb-32" ref={scrollRef} style={{ paddingBottom: '200px', WebkitOverflowScrolling: 'touch' }}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8" style={{ paddingLeft: 'max(16px, calc(16px + env(safe-area-inset-left, 0px)))', paddingRight: 'max(16px, calc(16px + env(safe-area-inset-right, 0px)))' }}>
            {messages.length === 0 && !isStreaming ? (
              /* Welcome Screen - Grok Style */
              <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <div className="mb-8">
                  <GrokLogo size={120} className="text-foreground opacity-30" />
                </div>
                <h1 className="text-4xl font-semibold text-foreground mb-2">ChatTLS</h1>
                <p className="text-muted-foreground text-lg mb-12">What do you want to know?</p>
              </div>
            ) : (
              /* Messages */
              <div className="space-y-8 pb-6">
                <AnimatePresence mode="popLayout">
                  {messages.map((message: Message) => (
                    <motion.div
                      key={message.id}
                      className="space-y-4"
                      {...messageAnimation}
                      layout
                    >
                      {message.role === "user" ? (
                        <div className="flex justify-end my-6">
                          <div className="bg-primary/10 backdrop-blur-md rounded-2xl rounded-tr-sm px-5 py-4 max-w-2xl ml-auto border border-primary/10 shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <p className="text-base text-foreground font-medium">{message.content}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="my-8 group relative">
                          <div className="bg-card/30 backdrop-blur-md rounded-2xl rounded-tl-sm px-6 py-6 shadow-lg border border-white/5 hover:shadow-xl transition-all duration-300">
                            <div className="text-base text-foreground leading-relaxed max-w-full">
                              <div className="prose prose-base max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-li:text-foreground prose-a:text-primary prose-code:text-foreground">
                                <ReactMarkdown 
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    a: ({ href, children }) => {
                                      const text = String(children);
                                      // Check if this is a citation: web source (e.g., "Bankrate +2") or document (e.g., "TLS Guidelines 43%")
                                      const isWebCitation = /^[A-Z][a-z]+(\s\+\d+)?$/.test(text);
                                      const isDocCitation = /\d+%$/.test(text); // Ends with percentage
                                      const isNumberCitation = /^\[?\d+\]?$/.test(text);
                                      
                                      if (isWebCitation || isDocCitation || isNumberCitation) {
                                        return (
                                          <a
                                            href={href}
                                            target={href?.startsWith('/') ? '_self' : '_blank'}
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium bg-zinc-700/80 text-zinc-200 hover:bg-zinc-600 rounded-full ml-1 no-underline transition-all duration-200 hover:scale-105 border border-zinc-600/50"
                                            title={href}
                                          >
                                            {text}
                                          </a>
                                        );
                                      }
                                      // Regular link
                                      return (
                                        <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                          {children}
                                        </a>
                                      );
                                    }
                                  }}
                                >
                                  {message.content}
                                </ReactMarkdown>
                              </div>
                            </div>
                          </div>
                          {/* Copy Button - appears on hover */}
                          <button
                            onClick={() => copyToClipboard(message.content, message.id)}
                            className={`absolute top-2 right-2 bg-background/50 backdrop-blur-sm border border-white/10 rounded-full p-2 hover:bg-background/80 transition-all duration-200 shadow-sm ${copiedMessageId === message.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                              }`}
                          >
                            {copiedMessageId === message.id ? (
                              <Check className="w-4 h-4 text-foreground" />
                            ) : (
                              <Copy className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                            )}
                          </button>
                          <div className="flex items-center gap-3 mt-3 px-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const vote = messageRatings[message.id] === 'up' ? undefined : 'up';
                                if (vote) {
                                  rateMessageMutation.mutate({ messageId: message.id, vote });
                                }
                              }}
                              className={`h-8 px-3 rounded-full backdrop-blur-sm ${messageRatings[message.id] === 'up'
                                ? 'bg-green-500/10 text-green-500 hover:bg-green-500/20'
                                : 'bg-white/5 text-muted-foreground hover:text-foreground hover:bg-white/10'
                                } transition-all duration-200`}
                            >
                              <ThumbsUp className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const vote = messageRatings[message.id] === 'down' ? undefined : 'down';
                                if (vote) {
                                  rateMessageMutation.mutate({ messageId: message.id, vote });
                                }
                              }}
                              className={`h-8 px-3 rounded-full backdrop-blur-sm ${messageRatings[message.id] === 'down'
                                ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                                : 'bg-white/5 text-muted-foreground hover:text-foreground hover:bg-white/10'
                                } transition-all duration-200`}
                            >
                              <ThumbsDown className="w-4 h-4" />
                            </Button>
                            <span className="text-xs text-muted-foreground/60 font-medium ml-1">
                              {message.createdAt ? format(parseISO(typeof message.createdAt === 'string' ? message.createdAt : message.createdAt.toISOString()), "h:mm a") : ""}
                            </span>
                          </div>
                          {/* Action Buttons */}
                          <div className="mt-3 px-2 flex items-center gap-2 flex-nowrap overflow-x-auto pb-1 scrollbar-hide">
                            <button
                              onClick={() => handleComposeResponse(message.id)}
                              disabled={isComposing && composingResponseFor === message.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-white/10 bg-white/5 backdrop-blur-sm text-muted-foreground hover:text-foreground hover:bg-white/10 hover:border-white/20 hover:shadow-sm hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap disabled:hover:translate-y-0"
                            >
                              {isComposing && composingResponseFor === message.id ? (
                                <>
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Composing...
                                </>
                              ) : (
                                <>
                                  <Mail className="w-3 h-3" />
                                  Compose reply
                                </>
                              )}
                            </button>
                            {(() => {
                              const messageIndex = messages.findIndex((m: Message) => m.id === message.id);
                              const previousUserMessage = messageIndex > 0 ? messages[messageIndex - 1] : null;
                              return previousUserMessage && previousUserMessage.role === 'user' ? (
                                <button
                                  onClick={() => {
                                    if (!currentConversationId) {
                                      toast({
                                        title: "Error",
                                        description: "No active conversation. Please start a new conversation.",
                                        variant: "destructive",
                                      });
                                      return;
                                    }
                                    if (!previousUserMessage?.content) {
                                      toast({
                                        title: "Error",
                                        description: "Unable to check exceptions. Previous message not found.",
                                        variant: "destructive",
                                      });
                                      return;
                                    }
                                    if (previousUserMessage?.content && currentConversationId) {
                                      streamMessage(currentConversationId, previousUserMessage.content, true);
                                    } else {
                                      toast({
                                        title: "Error",
                                        description: "Unable to check exceptions. Please try again.",
                                        variant: "destructive",
                                      });
                                    }
                                  }}
                                  disabled={isStreaming}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-white/10 bg-white/5 backdrop-blur-sm text-muted-foreground hover:text-foreground hover:bg-white/10 hover:border-white/20 hover:shadow-sm hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap disabled:hover:translate-y-0"
                                >
                                  <Shield className="w-3 h-3" />
                                  Search Exceptions
                                </button>
                              ) : null;
                            })()}
                          </div>
                          {/* Sources section removed - now shown as inline citations within each section */}
                          {suggestedQuestions && suggestedQuestions.messageId === message.id && (
                            <div className="flex flex-wrap gap-2 mt-4 px-2">
                              {suggestedQuestions.questions.map((question: string, idx: number) => (
                                <Button
                                  key={idx}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleSuggestedQuestion(question)}
                                  className="text-sm rounded-full border-white/10 bg-white/5 backdrop-blur-sm text-muted-foreground hover:text-foreground hover:border-primary/50 hover:bg-primary/10 hover:shadow-sm transition-all duration-200"
                                >
                                  {question}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Streaming Message */}
                <AnimatePresence>
                  {isStreaming && (
                    <motion.div
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={springTransition}
                      className="my-8"
                    >
                      {/* Processing Steps - 3 Search Agents with Animated Status */}
                      {processingSteps.length > 0 && (
                        <div className="mb-6 space-y-3 px-2">
                          {processingSteps.map((step, index) => (
                            <motion.div
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              key={`${step.step}-${index}`}
                              className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-300 shadow-sm ${
                                step.status === 'active'
                                  ? 'bg-primary/5 border-primary/20 text-foreground ring-1 ring-primary/10'
                                  : step.status === 'complete'
                                    ? 'bg-green-500/5 border-green-500/20 text-muted-foreground'
                                    : 'bg-card/30 border-white/5 text-muted-foreground/50'
                              }`}
                            >
                              {step.status === 'active' && (
                                <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
                              )}
                              {step.status === 'complete' && (
                                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                              )}
                              {step.status === 'pending' && (
                                <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/20 flex-shrink-0" />
                              )}
                              <div className="flex flex-col min-w-0">
                                <span className={`text-sm ${step.status === 'active' ? 'font-medium' : ''}`}>
                                  {step.step}
                                </span>
                                {step.message && step.status === 'active' && (
                                  <motion.span 
                                    key={step.message}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="text-xs text-primary/70 truncate"
                                  >
                                    {step.message}
                                  </motion.span>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}

                      {streamingMessage && (
                        <div className="bg-card/30 backdrop-blur-md rounded-2xl rounded-tl-sm px-6 py-6 shadow-lg border border-white/5">
                          <div className="text-base text-foreground leading-relaxed">
                            <div className="prose prose-base max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-a:text-primary prose-code:text-foreground">
                              <ReactMarkdown 
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  a: ({ href, children }) => {
                                    const text = String(children);
                                    // Check if this is a citation: web source (e.g., "Bankrate +2") or document (e.g., "TLS Guidelines 43%")
                                    const isWebCitation = /^[A-Z][a-z]+(\s\+\d+)?$/.test(text);
                                    const isDocCitation = /\d+%$/.test(text); // Ends with percentage
                                    const isNumberCitation = /^\[?\d+\]?$/.test(text);
                                    
                                    if (isWebCitation || isDocCitation || isNumberCitation) {
                                      return (
                                        <a
                                          href={href}
                                          target={href?.startsWith('/') ? '_self' : '_blank'}
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium bg-zinc-700/80 text-zinc-200 hover:bg-zinc-600 rounded-full ml-1 no-underline transition-all duration-200 hover:scale-105 border border-zinc-600/50"
                                          title={href}
                                        >
                                          {text}
                                        </a>
                                      );
                                    }
                                    // Regular link
                                    return (
                                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                        {children}
                                      </a>
                                    );
                                  }
                                }}
                              >
                                {streamingMessage}
                              </ReactMarkdown>
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

          </div>
        </ScrollArea>

        {/* Input Bar - Floating Glossy Style */}
        <div
          className="fixed bottom-0 left-0 md:left-[240px] right-0 z-40 p-4 sm:p-6 pointer-events-none"
        >
          <div className="max-w-3xl mx-auto pointer-events-auto">
            {/* Product Filter - Above input, expands upward */}
            <div className="mb-3 flex items-center gap-2">
              <Popover open={productFilterOpen} onOpenChange={setProductFilterOpen}>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-white/10 bg-black/20 backdrop-blur-md rounded-full border border-white/10 transition-all duration-200 hover:border-white/20 hover:shadow-sm">
                    <span className="text-xs font-medium">Product:</span>
                    <span className="font-medium text-foreground">
                      {productFilter === "all" ? "All Products" : productFilter}
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${productFilterOpen ? 'rotate-180' : ''}`} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  className="w-56 p-1 bg-card/90 backdrop-blur-xl border-white/10 shadow-xl rounded-xl"
                  sideOffset={8}
                >
                  <div className="space-y-0.5">
                    {/* Filter options remain the same but with updated styles if needed */}
                    <button
                      onClick={() => {
                        setProductFilter("all");
                        setProductFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${productFilter === "all"
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
                        }`}
                    >
                      All Products
                    </button>
                    {/* ... other buttons ... */}
                    {["VA", "FHA", "DSCR", "Conforming", "Jumbo", "HELOC", "Bank Statement", "Flex Equity Income", "USDA", "VA IRRRL", "ONCF", "AUS INV+"].map((filter) => (
                      <button
                        key={filter}
                        onClick={() => {
                          setProductFilter(filter);
                          setProductFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${productFilter === filter
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
                          }`}
                      >
                        {filter}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="bg-card/80 backdrop-blur-xl border border-white/10 rounded-[2rem] shadow-2xl p-2 flex items-end gap-2 relative transition-all duration-300 hover:border-white/20 hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
              {selectedFile && (
                <div className="absolute -top-12 left-0 flex items-center gap-2 bg-card/90 backdrop-blur-md border border-white/10 rounded-xl px-4 py-2 shadow-lg animate-in fade-in slide-in-from-bottom-2">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="text-sm text-foreground font-medium flex-1 truncate max-w-[200px]">{selectedFile.name}</span>
                  <span className="text-xs text-muted-foreground">{(selectedFile.size / 1024).toFixed(2)} KB</span>
                  <button
                    onClick={clearSelectedFile}
                    className="ml-2 text-muted-foreground hover:text-destructive transition-colors p-1 hover:bg-white/10 rounded-full"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="flex-1 relative flex items-end">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-3 text-muted-foreground hover:text-foreground hover:bg-white/10 rounded-full transition-colors mb-[1px]"
                  title="Attach file"
                >
                  <Paperclip className="w-5 h-5" />
                </button>

                <button
                  onClick={handleVoiceClick}
                  className={`p-3 transition-colors rounded-full mb-[1px] ${isListening
                    ? 'text-red-500 hover:text-red-600 bg-red-500/10 animate-pulse'
                    : 'text-muted-foreground hover:text-foreground hover:bg-white/10'
                    }`}
                  type="button"
                  title={isListening ? "Stop listening" : "Start voice input"}
                >
                  <Mic className={`w-5 h-5 ${isListening ? 'fill-current' : ''}`} />
                </button>

                <Textarea
                  ref={textareaRef}
                  value={messageInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setMessageInput(value);
                    // Auto-resize textarea
                    if (textareaRef.current) {
                      textareaRef.current.style.height = "auto";
                      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + "px";
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="What do you want to know?"
                  className="w-full bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-2 py-3.5 text-base text-foreground placeholder:text-muted-foreground resize-none overflow-hidden min-h-[52px] max-h-[120px]"
                  rows={1}
                  style={{ fontSize: '16px', textAlign: 'left' }}
                />
              </div>

              <Button
                onClick={() => handleSendMessage()}
                disabled={!messageInput.trim() || isStreaming}
                className="rounded-full w-12 h-12 p-0 bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-primary/25 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 mb-[1px]"
              >
                {isStreaming ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5 ml-0.5" />
                )}
              </Button>
            </div>
          </div>
          <div className="max-w-3xl mx-auto pt-3 pointer-events-auto">
            <p className="text-[10px] sm:text-xs text-muted-foreground/60 text-center">
              Chat TLS is powered by AI. Verify important info with your AE.
            </p>
          </div>
        </div>
      </main>

      {/* Delete Dialog - Grok Dark Theme */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border border-border text-foreground shadow-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Delete Conversation?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              This will permanently delete this conversation and all its messages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-muted border border-border text-foreground hover:bg-muted/80">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="border border-destructive bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Compose Response Dialog */}
      <Dialog open={showComposeDialog} onOpenChange={setShowComposeDialog}>
        <DialogContent className="bg-card border border-border text-foreground shadow-xl max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">Composed Response</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This response is ready to copy and paste into your email or message.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {composedResponse && !isRewriting ? (
              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <div className="prose prose-sm max-w-none dark:prose-invert prose-p:text-foreground prose-strong:text-foreground prose-li:text-foreground">
                  <p className="whitespace-pre-wrap text-foreground leading-relaxed">{composedResponse}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">
                  {isRewriting ? 'Rewriting response...' : 'Composing response...'}
                </span>
              </div>
            )}
          </div>
          <DialogFooter className="flex flex-col gap-3">
            {/* Rewrite Options */}
            {composedResponse && !isRewriting && (
              <div className="flex flex-wrap gap-2 w-full pb-2 border-b border-border">
                <span className="text-sm text-muted-foreground self-center mr-2">Modify:</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (composedResponse && selectedMessageId) {
                      setIsRewriting(true);
                      rewriteResponseMutation.mutate({
                        messageId: selectedMessageId,
                        currentResponse: composedResponse,
                        modification: 'shorter'
                      });
                    }
                  }}
                  disabled={isRewriting}
                  className="text-xs"
                >
                  {isRewriting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                  Shorter
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (composedResponse && selectedMessageId) {
                      setIsRewriting(true);
                      rewriteResponseMutation.mutate({
                        messageId: selectedMessageId,
                        currentResponse: composedResponse,
                        modification: 'more-professional'
                      });
                    }
                  }}
                  disabled={isRewriting}
                  className="text-xs"
                >
                  More Professional
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (composedResponse && selectedMessageId) {
                      setIsRewriting(true);
                      rewriteResponseMutation.mutate({
                        messageId: selectedMessageId,
                        currentResponse: composedResponse,
                        modification: 'more-casual'
                      });
                    }
                  }}
                  disabled={isRewriting}
                  className="text-xs"
                >
                  More Casual
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (composedResponse && selectedMessageId) {
                      setIsRewriting(true);
                      rewriteResponseMutation.mutate({
                        messageId: selectedMessageId,
                        currentResponse: composedResponse,
                        modification: 'more-friendly'
                      });
                    }
                  }}
                  disabled={isRewriting}
                  className="text-xs"
                >
                  More Friendly
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (composedResponse && selectedMessageId) {
                      setIsRewriting(true);
                      rewriteResponseMutation.mutate({
                        messageId: selectedMessageId,
                        currentResponse: composedResponse,
                        modification: 'rewrite'
                      });
                    }
                  }}
                  disabled={isRewriting}
                  className="text-xs"
                >
                  Rewrite
                </Button>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-row gap-2 w-full justify-end">
              <Button
                variant="outline"
                onClick={() => setShowComposeDialog(false)}
                className="bg-muted border-border text-foreground hover:bg-muted/80 hover:border-border/80"
              >
                Close
              </Button>
              <Button
                onClick={async () => {
                  if (composedResponse) {
                    try {
                      await navigator.clipboard.writeText(composedResponse);
                      toast({
                        title: "Copied!",
                        description: "Response copied to clipboard",
                      });
                    } catch (err) {
                      toast({
                        title: "Error",
                        description: "Failed to copy. Please try again.",
                        variant: "destructive",
                      });
                    }
                  }
                }}
                disabled={!composedResponse || isRewriting}
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Response
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Format Message Dialog */}
      <Dialog open={showFormatDialog} onOpenChange={setShowFormatDialog}>
        <DialogContent className="bg-card border border-border text-foreground shadow-xl max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {formattingType === 'email' ? 'Formatted for Email' : 'Formatted for Text Message'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This message is ready to copy and paste directly into {formattingType === 'email' ? 'an email' : 'a text message'}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {formattedMessage ? (
              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <p className="whitespace-pre-wrap text-foreground leading-relaxed">{formattedMessage}</p>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          <DialogFooter className="flex flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowFormatDialog(false);
                setFormattedMessage(null);
                setFormattingType(null);
              }}
              className="bg-muted border-border text-foreground hover:bg-muted/80 hover:border-border/80"
            >
              Close
            </Button>
            <Button
              onClick={async () => {
                if (formattedMessage) {
                  try {
                    await navigator.clipboard.writeText(formattedMessage);
                    toast({
                      title: "Copied!",
                      description: `Message copied to clipboard`,
                    });
                  } catch (err) {
                    toast({
                      title: "Error",
                      description: "Failed to copy. Please try again.",
                      variant: "destructive",
                    });
                  }
                }
              }}
              disabled={!formattedMessage}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Leaderboard Sliding Panel */}
      {showLeaderboard && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setShowLeaderboard(false)}
          />

          {/* Leaderboard Panel */}
          <div
            className={`fixed top-0 right-0 h-full w-80 bg-card border-l border-border shadow-2xl z-50 transform transition-transform duration-300 ${showLeaderboard ? 'translate-x-0' : 'translate-x-full'
              }`}
            style={{
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Top Users
                </h2>
                <button
                  onClick={() => setShowLeaderboard(false)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Leaderboard Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {leaderboardData?.users && leaderboardData.users.length > 0 ? (
                  <div className="space-y-3">
                    {leaderboardData.users.map((user: any, index: number) => (
                      <div
                        key={user.userId}
                        className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border"
                      >
                        {/* Rank Badge */}
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${index === 0 ? 'bg-yellow-500 text-yellow-900' :
                          index === 1 ? 'bg-gray-400 text-gray-900' :
                            index === 2 ? 'bg-orange-600 text-orange-100' :
                              'bg-muted text-muted-foreground'
                          }`}>
                          {index + 1}
                        </div>

                        {/* User Info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">
                            {user.firstName || user.lastName
                              ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                              : user.username || user.email}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {user.messageCount} question{user.messageCount !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    No leaderboard data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
